
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import type { Company, CompanyFormData } from '@/types/company';

export const useCompanies = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { currentUser } = useAuth();

  const fetchCompanies = async () => {
    try {
      // Super admin vê todas as empresas
      // Admin vê apenas sua empresa
      // Usuário comum vê apenas sua empresa
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setCompanies(data || []);
    } catch (error: any) {
      console.error('Error fetching companies:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível carregar as empresas.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addCompany = async (company: CompanyFormData) => {
    try {
      // Apenas super_admin pode adicionar empresas
      if (currentUser?.type !== 'super_admin') {
        throw new Error('Você não tem permissão para adicionar empresas');
      }

      const { data, error } = await supabase
        .from('companies')
        .insert([{
          ...company,
          created_by: currentUser.id,
          updated_by: currentUser.id
        }])
        .select()
        .single();

      if (error) throw error;

      setCompanies(prev => [data, ...prev]);
      
      toast({
        title: "Sucesso",
        description: "Empresa criada com sucesso!",
      });

      return data;
    } catch (error: any) {
      console.error('Error adding company:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Erro ao criar empresa",
      });
      throw error;
    }
  };

  const updateCompany = async (id: string, updates: CompanyFormData) => {
    try {
      // Apenas super_admin pode atualizar qualquer empresa
      // Admin pode atualizar apenas sua própria empresa
      if (currentUser?.type !== 'super_admin' && currentUser?.companyId !== id) {
        throw new Error('Você não tem permissão para atualizar esta empresa');
      }

      const { data, error } = await supabase
        .from('companies')
        .update({
          ...updates,
          updated_by: currentUser?.id
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setCompanies(prev =>
        prev.map(company => (company.id === id ? { ...company, ...data } : company))
      );

      toast({
        title: "Sucesso",
        description: "Empresa atualizada com sucesso!",
      });

      return data;
    } catch (error: any) {
      console.error('Error updating company:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Erro ao atualizar empresa",
      });
      throw error;
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [currentUser]); // Refetch quando mudar o usuário atual

  return {
    companies,
    isLoading,
    addCompany,
    updateCompany,
    refreshCompanies: fetchCompanies,
  };
};
